import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import axiosInstance from '../../utils/axios';
import amplifyConfig from '../../config';
import { CONDITION_VIEW_MODES, MANAGE_CONDITION_TYPES, CONDITION_STATUS } from '../../constants';
import getOptionsRowNodeId from '../../utils/manage-condition/getOptionsRowNodeId';
import sortDetailCustomize from '../../utils/manage-condition/sortDetailCustomize';
import sortDetailOrdinary from '../../utils/manage-condition/sortDetailOrdinary';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { BusinessType, ErrorResponse, SteelCategory, SteelMaker } from '../../types/common';
import type {
  ApplyConditionOptionsSelectionParams,
  ConditionDBType,
  ConditionOptionsType,
  ConditionDetailType,
  ConditionState,
  ConditionViewMode,
  ConditionWithDetailType,
  CreateWhenNotExistParams,
  CreateConditionDetailParams,
  DeleteConditionDetailParams,
  ManageConditionType,
  ModalMode,
  ConditionOptionsSearchParams,
  ConditionOptionsData,
  UpdateConditionParams,
  UpdateConditionDetailParams,
  UpdateConditionWithDetailParams,
} from '../../types/condition';

const initialState: ConditionState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  selectedViewMode: CONDITION_VIEW_MODES.APPROVED,
  selectedTab: MANAGE_CONDITION_TYPES.ORDINARY,
  selectedSteelMaker: undefined,
  selectedBusinessType: undefined,
  selectedSteelCategory: undefined,
  conditionId: undefined,
  condition: undefined,
  conditionOrg: undefined,
  conditionOrdinary: undefined,
  searchParams: undefined,
  conditionOptions: undefined,
};

const slice = createSlice({
  name: 'Condition',
  initialState,
  reducers: {
    startLoading(state: ConditionState) {
      state.isLoading = true;
    },
    hasError(state: ConditionState, action: PayloadAction<string>) {
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },
    resetMessage(state: ConditionState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },
    setServerMessage(state: ConditionState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setDialogMode(state: ConditionState, action: PayloadAction<ModalMode>): void {
      state.dialogMode = action.payload;
    },
    openDialog(state: ConditionState): void {
      state.isDialogOpen = true;
    },
    closeDialog(state: ConditionState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.conditionOrdinary = undefined;
    },

    setSelectedViewMode(state: ConditionState, action: PayloadAction<ConditionViewMode>): void {
      state.selectedViewMode = action.payload;
    },

    setSelectedTab(state: ConditionState, action: PayloadAction<ManageConditionType>): void {
      state.selectedTab = action.payload;
    },

    selectSteelMaker(state: ConditionState, action: PayloadAction<SteelMaker>): void {
      state.selectedSteelMaker = action.payload;
      state.conditionOptions = [];
    },

    selectBusinessType(state: ConditionState, action: PayloadAction<BusinessType>): void {
      state.selectedBusinessType = action.payload;
      state.conditionOptions = [];
    },

    selectSteelCategory(state: ConditionState, action: PayloadAction<SteelCategory>): void {
      state.selectedSteelCategory = action.payload;
      state.conditionOptions = [];
    },

    undoDeleteOrdinary(state: ConditionState, action: PayloadAction<{ data: ConditionDetailType }>): void {
      state.isLoading = false;
      // 作成したデータを追加
      const newDetailOrdinary: ConditionDetailType[] = [...state.condition.detailOrdinary, action.payload.data];
      state.condition = Object.assign({}, state.condition, { conditionStatus: CONDITION_STATUS.EDITING, detailOrdinary: newDetailOrdinary });
    },
    resetCondition(state: ConditionState): void {
      state.selectedSteelMaker = undefined;
      state.selectedBusinessType = undefined;
      state.selectedSteelCategory = undefined;
      state.conditionId = undefined;
      state.condition = undefined;
      state.conditionOrg = undefined;
      state.conditionOrdinary = undefined;
      state.searchParams = undefined;
      state.conditionOptions = undefined;
    },

    // Condition
    // ================================================================================
    getCondition(state: ConditionState, action: PayloadAction<ConditionWithDetailType>): void {
      state.isLoading = false;
      const sortedOrdinary: ConditionDetailType[] = action.payload.detailOrdinary.sort(sortDetailOrdinary);
      const sortedOrdinaryApprove: ConditionDetailType[] = action.payload.detailOrdinaryApprove.sort(sortDetailOrdinary);
      const sortedCustomize: ConditionDetailType[] = action.payload.detailCustomize.sort(sortDetailCustomize);
      const sortedCustomizeApprove: ConditionDetailType[] = action.payload.detailCustomizeApprove.sort(sortDetailCustomize);
      const newCondition: ConditionWithDetailType = Object.assign(
        {},
        action.payload,
        { detailOrdinary: sortedOrdinary },
        { detailOrdinaryApprove: sortedOrdinaryApprove },
        { detailCustomize: sortedCustomize },
        { detailCustomizeApprove: sortedCustomizeApprove },
      );
      state.condition = newCondition;
      // state.condition = action.payload;
      state.conditionOrg = action.payload;
    },
    confirmCondition(state: ConditionState, action: PayloadAction<ConditionDBType>): void {
      state.isLoading = false;
      state.conditionId = action.payload.id;
    },
    updateCondition(state: ConditionState, action: PayloadAction<UpdateConditionParams>): void {
      state.isLoading = false;
      state.condition = Object.assign({}, state.condition, action.payload);
    },
    deleteCondition(state: ConditionState, action: PayloadAction<ConditionWithDetailType>): void {
      state.isLoading = false;
    },

    // ConditionOrdinary
    // ================================================================================

    setConditionOrdinary(state: ConditionState, action: PayloadAction<number>): void {
      const id: number | undefined = action.payload;
      if (id && state.condition && state.condition.detailOrdinary.length > 0) {
        const detailOrdinary: ConditionDetailType | undefined = state.condition.detailOrdinary.find((detail) => detail.id === id);
        state.conditionOrdinary = detailOrdinary;
      } else {
        state.conditionOrdinary = undefined;
      }
    },
    createConditionOrdinary(state: ConditionState, action: PayloadAction<ConditionDetailType>): void {
      state.isLoading = false;
      const newDetailOrdinary: ConditionDetailType[] = [...state.condition.detailOrdinary, action.payload];
      state.condition = Object.assign({}, state.condition, { conditionStatus: CONDITION_STATUS.EDITING, detailOrdinary: newDetailOrdinary });
    },
    updateConditionOrdinary(state: ConditionState, action: PayloadAction<ConditionDetailType>): void {
      state.isLoading = false;
      // conditionの情報をアップデート
      const newDetail: ConditionDetailType = action.payload;
      const newDetailOrdinary: ConditionDetailType[] | undefined = state.condition
        ? state.condition.detailOrdinary.map((detail) => {
            if (detail.id === newDetail.id) {
              return newDetail;
            } else {
              return detail;
            }
          })
        : undefined;
      if (newDetailOrdinary !== undefined) {
        state.condition = Object.assign({}, state.condition, { conditionStatus: CONDITION_STATUS.EDITING, detailOrdinary: newDetailOrdinary });
      } else {
        // do nothing
      }
      // conditionOrdinaryの情報をアップデート
      state.conditionOrdinary = action.payload;
    },
    deleteConditionOrdinary(state: ConditionState, action: PayloadAction<ConditionDetailType>): void {
      state.isLoading = false;
      const deletedDetail = action.payload;
      const newDetailOrdinary: ConditionDetailType[] | undefined = state.condition
        ? state.condition.detailOrdinary.filter((detail) => {
            if (detail.id === deletedDetail.id) {
              return false;
            } else {
              return true;
            }
          })
        : undefined;
      if (newDetailOrdinary !== undefined) {
        state.condition = Object.assign({}, state.condition, { conditionStatus: CONDITION_STATUS.EDITING, detailOrdinary: newDetailOrdinary });
      } else {
        // do nothing
      }
    },

    // ConditionCustomize
    // ================================================================================

    setSearchParams(state: ConditionState, action: PayloadAction<ConditionOptionsSearchParams>): void {
      state.searchParams = action.payload;
    },
    getConditionOptions(state: ConditionState, action: PayloadAction<ConditionOptionsType[]>): void {
      state.isLoading = false;
      state.conditionOptions = action.payload;
      // const newConditionOptions: ConditionOptionsType[] = action.payload.sort(sortDetailCustomize);
      // state.conditionOptions = newConditionOptions;
    },
    applyConditionOptionsSelection(state: ConditionState, action: PayloadAction<ApplyConditionOptionsSelectionParams>): void {
      if (state.condition !== undefined) {
        // 件数が多くても処理が重くならないよう、照合は rowNodeId の Set で行う
        const removeRowNodeIds: Set<string> = new Set(action.payload.toRemove.map((option) => getOptionsRowNodeId(option)));
        const remainedDetailCustomizes = state.condition.detailCustomize.filter((detail) => !removeRowNodeIds.has(getOptionsRowNodeId(detail)));
        // 同じ詳細可視制御がなければ追加する
        const existingRowNodeIds: Set<string> = new Set(remainedDetailCustomizes.map((detail) => getOptionsRowNodeId(detail)));
        const detailCustomizesToAdd: ConditionOptionsData[] = action.payload.toAdd.filter((option) => {
          const rowNodeId: string = getOptionsRowNodeId(option);
          if (existingRowNodeIds.has(rowNodeId)) {
            return false;
          } else {
            existingRowNodeIds.add(rowNodeId);
            return true;
          }
        });
        state.condition.detailCustomize = [...remainedDetailCustomizes, ...detailCustomizesToAdd];
      } else {
        // do nothing
      }
    },
    addOptionToConditionCustomize(state: ConditionState, action: PayloadAction<ConditionOptionsData>): void {
      if (state.condition) {
        // 同じ詳細可視制御がなければ追加する
        const rowNodeId: string = getOptionsRowNodeId(action.payload);
        const sameDetailCustomize: ConditionOptionsData | ConditionOptionsType | undefined = state.condition.detailCustomize?.find((detail) => {
          if (rowNodeId === getOptionsRowNodeId(detail)) {
            return true;
          }
        });
        if (sameDetailCustomize === undefined) {
          state.condition.detailCustomize = [...state.condition.detailCustomize, action.payload];
        }
      }
    },
    removeOptionFromConditionCustomize(state: ConditionState, action: PayloadAction<ConditionOptionsData>): void {
      if (state.condition !== undefined) {
        const optionRowNodeId: string = getOptionsRowNodeId(action.payload);
        state.condition.detailCustomize = state.condition.detailCustomize.filter((detail) => {
          const detailRowNodeId: string = getOptionsRowNodeId(detail);
          if (detailRowNodeId === optionRowNodeId) {
            return false;
          } else {
            return true;
          }
        });
      } else {
        // do nothing
      }
    },
    updateConditionCustomize(state: ConditionState, action: PayloadAction<ConditionWithDetailType>): void {
      state.isLoading = false;
      // console.log(action.payload);
      state.condition = action.payload;
      state.conditionOrg = action.payload;
    },
  },
});

export const { reducer, actions } = slice;

const asyncErrorHandler = (err, dispatch) => {
  if (err.isAxiosError) {
    // err: AxiosError
    if (err.response) {
      const serverResponse: ErrorResponse = err.response.data;
      dispatch(slice.actions.hasError(serverResponse.message));
    } else {
      dispatch(slice.actions.hasError(err.message));
    }
  } else {
    // err: Error
    dispatch(slice.actions.hasError(err.message));
  }
};

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const showErrorMessage =
  (message: string): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  };

export const setDialogMode =
  (mode: ModalMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogMode(mode));
  };
export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };
export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const setSelectedViewMode =
  (mode: ConditionViewMode): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setSelectedViewMode(mode));
  };
export const setSelectedTab =
  (tabName: ManageConditionType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setSelectedTab(tabName));
  };
export const selectSteelMaker =
  (steelMaker: SteelMaker): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectSteelMaker(steelMaker));
  };
export const selectBusinessType =
  (businessType: BusinessType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectBusinessType(businessType));
  };
export const selectSteelCategory =
  (steelCategory: SteelCategory): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectSteelCategory(steelCategory));
  };

export const undoDeleteOrdinary =
  (params: CreateConditionDetailParams): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startLoading());

    try {
      const conditionId: number = params.conditionId;
      const response: AxiosResponse<ConditionDetailType> = await axiosInstance.post(amplifyConfig.apiGateway.URL + '/condition-detail/' + String(conditionId), params);
      const data = response.data;
      dispatch(slice.actions.undoDeleteOrdinary({ data }));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };
export const resetCondition =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetCondition());
  };

// Condition

export const confirmCondition =
  (params: CreateWhenNotExistParams): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response: AxiosResponse<ConditionDBType> = await axiosInstance.post(amplifyConfig.apiGateway.URL + '/condition/createWhenNotExist', params);
      dispatch(slice.actions.confirmCondition(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const getCondition =
  (id: number): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response: AxiosResponse<ConditionWithDetailType> = await axiosInstance.get(amplifyConfig.apiGateway.URL + '/condition/withDetail/' + String(id));
      dispatch(slice.actions.getCondition(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const updateCondition =
  (id: number, params: UpdateConditionParams): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startLoading());
    try {
      const response: AxiosResponse<ConditionDBType> = await axiosInstance.put(amplifyConfig.apiGateway.URL + '/condition/' + String(id), params);
      dispatch(slice.actions.updateCondition(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const deleteCondition =
  (id: number): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(id);
    dispatch(slice.actions.startLoading());
    try {
      const response: AxiosResponse<ConditionWithDetailType> = await axiosInstance.delete(amplifyConfig.apiGateway.URL + '/condition/' + String(id));
      dispatch(slice.actions.deleteCondition(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

// Condition Ordinary

export const setConditionOrdinary =
  (id?: number): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setConditionOrdinary(id));
  };

export const createConditionOrdinary =
  (params: CreateConditionDetailParams): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startLoading());
    try {
      const conditionId: number = params.conditionId;
      const response: AxiosResponse<ConditionDetailType> = await axiosInstance.post(amplifyConfig.apiGateway.URL + '/condition-detail/' + String(conditionId), params);
      dispatch(slice.actions.createConditionOrdinary(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const updateConditionOrdinary =
  (params: UpdateConditionDetailParams): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startLoading());
    try {
      const conditionId: number = params.conditionId;
      const response: AxiosResponse<ConditionDetailType> = await axiosInstance.put(amplifyConfig.apiGateway.URL + '/condition-detail/' + String(conditionId) + '/' + String(params.id), params);
      dispatch(slice.actions.updateConditionOrdinary(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const deleteConditionOrdinary =
  (params: DeleteConditionDetailParams): AppThunk =>
  async (dispatch): Promise<void> => {
    const { id, conditionId, ...rest } = params;
    // console.log(id);
    dispatch(slice.actions.startLoading());
    try {
      const response: AxiosResponse<ConditionDetailType> = await axiosInstance.put(amplifyConfig.apiGateway.URL + '/condition-detail/delete/' + String(conditionId) + '/' + String(id), rest);
      dispatch(slice.actions.deleteConditionOrdinary(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

// Condition Customize

export const setSearchParams =
  (params: ConditionOptionsSearchParams): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setSearchParams(params));
  };

export const applyConditionOptionsSelection =
  (params: ApplyConditionOptionsSelectionParams): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.applyConditionOptionsSelection(params));
  };

export const addOptionToConditionCustomize =
  (params: ConditionOptionsData): AppThunk =>
  (dispatch): void => {
    // console.log(params);
    dispatch(slice.actions.addOptionToConditionCustomize(params));
  };

export const removeOptionFromConditionCustomize =
  (params: ConditionOptionsData): AppThunk =>
  (dispatch): void => {
    // console.log(params);
    dispatch(slice.actions.removeOptionFromConditionCustomize(params));
  };

export const getConditionOptions =
  (params: ConditionOptionsSearchParams): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    try {
      const response: AxiosResponse<ConditionOptionsType[]> = await axiosInstance.get(amplifyConfig.apiGateway.URL + '/millsheet/condition-detail-options', {
        params,
      });
      dispatch(slice.actions.getConditionOptions(response.data));
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export const updateConditionCustomize =
  (params: UpdateConditionWithDetailParams): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startLoading());
    try {
      const conditionId: number | undefined = getState().condition.condition?.id;
      if (conditionId !== undefined) {
        const response: AxiosResponse<ConditionWithDetailType> = await axiosInstance.put(amplifyConfig.apiGateway.URL + '/condition/withCustomizeDetail/' + String(conditionId), params);
        dispatch(slice.actions.updateConditionCustomize(response.data));
      }
    } catch (err) {
      asyncErrorHandler(err, dispatch);
    }
  };

export default slice;
